/**
 * Main reseller dashboard content
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Link, Paper, TableContainer, Table, TableHead, TableBody, TableCell, TableRow, Tooltip, Typography
} from '@mui/material';

import { Add, CheckCircle, Cancel, CreditCard, Flag, HourglassEmpty, PauseCircleFilled, People } from '@mui/icons-material';
// PendingActions
import ResellerProblems from './ResellerProblems';
import LoadingSpinner from '../ui/LoadingSpinner'
import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';
import AppLogo from '../ui/AppLogo';

import FundingSourceTile from '../paymentsources/FundingSourceTile';
import AccountActivationSteps from '../activation/AccountActivationSteps';
import AccountActivationConfigurableSteps from '../activation/AccountActivationConfigurableSteps';
import AccountSuspendMsg from '../ui/AccountSuspendMsg';

import BalanceAndFloatWidgets from './BalanceAndFloatWidgets';
import ProjectBalanceAndFloatWidgets from './ProjectBalanceAndFloatWidgets';
import WalletBalanceWidgets from './WalletBalanceWidgets';
import SpendingWidgets from './SpendingWidgets';

import { green, red, amber } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();
const DashboardQuery = graphql`
query DashboardQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountName
    description
    contactLastName
    contactFirstName
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    maxCharge
    floatLimit
    accountStatus
    accountType
    useBudgets
    payFromBalance
    autoTopupActive
    autoTopupAmount
    autoTopupThreshhold
    maxMonthlyBudget
    personaInquiryId
    personaStatus
    verificationComplete
    walletTsByAccountId {
      totalCount
      nodes {
        walletId
        autoTopupActive
        autoTopupAmount
        autoTopupThreshold
      }
    }
    onboardingAccountTByAccountId {
      onboardingId
    }
    paymentSourceTsByAccountId(condition: {psStatus: 0} orderBy: PS_ORDER_ASC) {
      totalCount
      nodes {
        psId
        psType
        psStatus
        ccBrand
        ccLast4
        ccExpy
        fiName
        achAccountNum
        firstName
        lastName
        paypalEmail
        declineCount
        lastDeclineReason
        lastDeclineChargeAmount
      }
    }
    activeCards: bindCardTsByProvisioningAccountId (condition:{bcStatus: 0}) {
      totalCount
    }
    pendingCards: bindCardTsByProvisioningAccountId (condition:{bcStatus: -1}) {
      totalCount
    }
    subscriptions: customerAppTsByProvisioningAccountId(condition:{appStatus:0}) {
      totalCount
    }
    cardsOnFile: ccOnFileTsByAccountId {
      totalCount
    }
    appTsByAccountId {
      totalCount
      nodes {
        appId
        appName
        appSku
        appLogo
        customerAppTsByAppId(condition:{appStatus: 0}) {
          totalCount
          nodes {
            accountTByCustomerAccountId {
              accountId
              accountName
            }
          }
        }
      }
    }
    accountTsByParentId {
      totalCount
      nodes {
        accountId
        accountName
        contactFirstName
        contactLastName
        paymentSourceTsByAccountId(condition:{psStatus: 0}) {
          totalCount
        }
        activeCards: bindCardTsByUsingAccountId(condition:{bcStatus: 0}) {
          totalCount
        }
        pendingCards: bindCardTsByUsingAccountId(condition:{bcStatus: -1}) {
          totalCount
        }
        subscriptions: customerAppTsByCustomerAccountId(condition:{appStatus:0}) {
          totalCount
    		}
        cardsOnFile: ccOnFileTsByAccountId {
          totalCount
        }
      }
    }
    vppRTsByPartnerId {
      totalCount
      nodes {
        stage
        vppId
        vppTByVppId {
          programName
          description
          accountTByVendorId {
            logo
          }
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
        width: '100%',
    },
    rootLoggedIn: {
        flexGrow: 1,
        padding: 20,
    },
    button: {
      paddingLeft: 8,
    },
    buttonout: {
      paddingLeft: 8,
      marginBottom: 12,
    },
    buttonicon: {
      marginRight: 6,
    },
    instructButton: {
      marginTop: -1,
      marginLeft: 3,
      marginRight: 3,
    },
    instructText: {
      marginTop: 8,
      // marginLeft: -11,
    },
    paper: {
      padding: theme.spacing(2),
      // color: theme.palette.text.secondary,
    },
    mainContainer: {
      // marginTop: theme.spacing(1),
      // color: theme.palette.text.secondary,
    },
    dbTile: {
      [theme.breakpoints.up('xs')]: {
        padding: theme.spacing(1),
      },
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(1),
      },
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(2),
      },
      height: 56,
      width: 330
    },
    programInfo: {
      float: 'left',
    },
    backupProgramInfo: {
      maxWidth: 240,
      [theme.breakpoints.up('xs')]: {
        width: 'calc(100% - 60px - 24px)',
      },
      [theme.breakpoints.up('sm')]: {
        width: 240,
      },
    },
    section: {
        marginTop: 12,
        display: 'flex',
        flexWrap: 'wrap',
    },
    gridItem: {
      minWidth: 120,
    },
    tileWrapper: {
      minWidth: 240,
      marginTop: 16,
      marginLeft: 12,
      marginRight: 12,
      //padding: 12,
    },
    iconp: {
        marginTop: 12
    },
    appnameitem: {
      marginLeft: 8,
    },
    rtable: {
      marginTop: 16,
    },
    moretext: {
      height: 30,
      width: 172,
      paddingTop: 26,
      paddingBottom: 16,
      paddingLeft: 28,
    },
    appgrid: {
      minWidth: 136,
    },
    custlink: {
      marginTop: 4,
      marginBottom: 4,
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 24,
    },
    tblHeadCell: {
      textAlign: 'left',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    tblSumCell: {
      textAlign: 'left'
    },
    tblSumTxt: {
      marginTop: 8,
      marginBottom: 8,
      fontWeight: 'bold'
    }
});

class Dashboard extends Component {

  constructor(props) {
    super(props);

    this.state = {
      currentMaxCharge: 0,
      currentFloatLimit: 0,
      maxCharge: 0,
      floatLimit: 0,
    };  
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  render () {
    const { logError } = this.props.auth;

    const classes = this.props.classes;

    const statusOk = green[300];
    const statusSuspended = red[300];
    const statusCaution = amber[400];

    let acctId = this.props.resellerId;

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={DashboardQuery}
        variables={{accountId: acctId}}
        render={({error, props})=> {
          if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
          } else if (props) {
            //temporary code
            let newActivationOn = false;
            if (localStorage.getItem("verification") === "1") {
              newActivationOn = true;
            }

            let subOrder = false;
            // Sub order should be shown if budgets are on.
            if (abEnvironment.getUserAttribute('a_useBudgets') !== '0') {
              subOrder = true;
            }

            let theAccount = props.accountTByAccountId;
            let theBudget = theAccount.maxMonthlyBudget;

            let theWallet = null; 
            if (theAccount.walletTsByAccountId.totalCount > 0) {
              theWallet = theAccount.walletTsByAccountId.nodes[0] 
            }

            let acctType = theAccount.accountType;

            let paymentSourceProblems = false;
            let paymentSourceCount = theAccount.paymentSourceTsByAccountId.totalCount;
            let paymentSourceNodes = null;
            if (paymentSourceCount > 0) {
              // We only want the payment sources that can pay for subscriptions.
              let tempNodes = theAccount.paymentSourceTsByAccountId.nodes;
              for (let i = 0; i < tempNodes.length; i++) {
                let theNode = tempNodes[i];
                if (theNode.psType === 0 || theNode.psType === 1) {
                  if (paymentSourceNodes == null) {
                    paymentSourceNodes = [];
                  }
                  if (theNode.declineCount > 0) {
                    paymentSourceProblems = true;
                  }
                  paymentSourceNodes.push(theNode);
                }
              }
              if (paymentSourceNodes) {
                paymentSourceCount = paymentSourceNodes.length;
              } else {
                paymentSourceCount = 0;
              }
            }

            let cardsActiveCount  = theAccount.activeCards.totalCount;
            let cardsPendingCount = theAccount.pendingCards.totalCount;
            let cardsCount        = Number(cardsActiveCount) + Number(cardsPendingCount);
            let totalCards        = cardsCount;
            let subsCount         = Number(theAccount.subscriptions.totalCount);
            let customerCount     = theAccount.accountTsByParentId.totalCount;

            /* 
            // for testing
            paymentSourceCount = 0;
            subsCount = 0;
            customerCount = 0;
            */

            /** Analyze subscription data **/
            var theApps = [];
            if (theAccount.appTsByAccountId != null &&
                theAccount.appTsByAccountId.nodes != null)
            {
              let appNodes = theAccount.appTsByAccountId.nodes;
              for (let i = 0; i < appNodes.length; i++) {
                let sCount = 0;
                let acctList = [];
                if (appNodes[i].customerAppTsByAppId != null) {
                  sCount = appNodes[i].customerAppTsByAppId.totalCount; // Provisioned subscriptions
                  if (sCount === 0) {
                    // No subscriptions, so continue
                    continue;
                  }
                  let acctNodes = appNodes[i].customerAppTsByAppId.nodes;
                  for (let j = 0; j < acctNodes.length; j++) {
                    let aname = acctNodes[j].accountTByCustomerAccountId.accountName;
                    let aid   = acctNodes[j].accountTByCustomerAccountId.accountId;
                    if (aname != null) {
                      let attl = aname + '~~~' + aid;
                      // Is it already in the list?
                      let ti = acctList.findIndex((a) => a === attl);
                      if (! ti || ti === -1) {
                        acctList.push(attl);
                      }
                    }
                  }

                  // Sort
                  acctList.sort((a, b) => {
                    if (a > b) { return 1; }
                    if (a < b) { return -1; }
                    return 0;
                  });
                } else {
                  // No subscriptions, continue to next.
                  continue;
                }
                let anApp = {
                  appId:    appNodes[i].appId,
                  appName:  appNodes[i].appName,
                  appSku:   appNodes[i].appSku,
                  appLogo:  appNodes[i].appLogo,
                  subCount: sCount,
                  acctList: acctList
                }

                theApps.push(anApp);
              }

              // Sort in descending count order
              theApps.sort((a, b) => b.subCount - a.subCount);
            }
            let subsShown = 0;   // Counter for rows in subscription table
            let remSubsCount = Number(subsCount); // Running total of remaining subs
            
            let custsShown = 0;   // Counter for rows in customer table
            /** Analyze customer data **/
            var theCusts = [];
            if (theAccount.accountTsByParentId != null &&
                theAccount.accountTsByParentId.nodes != null)
            {
              let custNodes = theAccount.accountTsByParentId.nodes;

              for (let i = 0; i < custNodes.length; i++) {
                let pCount = custNodes[i].paymentSourceTsByAccountId.totalCount;
                let fCount = custNodes[i].cardsOnFile.totalCount;
                let cActiveCount  = custNodes[i].activeCards.totalCount;
                let cPendingCount = custNodes[i].pendingCards.totalCount;
                let cCount        = Number(cActiveCount) + Number(cPendingCount);
                totalCards = totalCards + cCount;
                let sCount = custNodes[i].subscriptions.totalCount;

                let aCust = {
                  custId:      custNodes[i].accountId,
                  custName:    custNodes[i].accountName,
                  custContact: custNodes[i].contactFirstName + ' ' + custNodes[i].contactLastName,
                  psCount:  pCount,
                  fCount:   fCount,
                  subCount: sCount
                }

                theCusts.push(aCust);
              }

              // Sort in descending count order
              theCusts.sort((a, b) => b.subCount - a.subCount);
            }

            let startingView = (subsCount === 0);
            //startingView = true;

            let payFromBalance = theAccount.payFromBalance;
            let autoTopupActive = theAccount.autoTopupActive;
            let autoTopupAmount = null;
            let autoTopupThreshold = null;
            if (autoTopupActive === 1) {
              if (theWallet) {
                if (Number(theWallet.autoTopupAmount) > 0 && Number(theWallet.autoTopupThreshold) > 0) {
                  autoTopupAmount = Number(theWallet.autoTopupAmount);
                  autoTopupThreshold = Number(theWallet.autoTopupThreshold);
                }
              } else {
                if (Number(theAccount.autoTopupAmount) > 0 && Number(theAccount.autoTopupThreshhold) > 0) {
                  autoTopupAmount = Number(theAccount.autoTopupAmount);
                  autoTopupThreshold = Number(theAccount.autoTopupThreshhold);
                }
   
              }
            }

            // Partner program info
            let vpps = null;
            if (theAccount.vppRTsByPartnerId.totalCount > 0) {
              vpps = theAccount.vppRTsByPartnerId.nodes;
            }

            //
            // Expensing
            // - If virtual cards have been provisioned or expensing is active, then show expensing.
            //
            let showExpensing = false;
            let expensingActive = false;
            let expensing = abEnvironment.getFlagValue("expensing");
            if (expensing != null && expensing.flagValue === 1) {
              expensingActive = true;
            }      
            
            let dashSubtitle = "Quick view of subscriptions and customers."
            if (totalCards > 0 || expensingActive === true) {
              showExpensing = true;
              dashSubtitle = "Quick view of funding sources, subscriptions, and customers."
            }

            return (
              <React.Fragment>
                {startingView &&
                <Grid item xs={12}>
                  <Typography variant="h4">
                    Manage subscriptions with AppBind
                  </Typography>
                  {/*<Typography variant="body1">
                    Create <strong>shared emails</strong> to sign up for subscriptions and <strong>virtual credit cards</strong> to pay for subscriptions. 
                    With AppBind, it is easy to manage and share subscriptions with your team and customers.
                  </Typography> */}
                  <Typography variant="body1">
                    Set up simple onboarding flows for your customers, and easily manage and share subscriptions.
                  </Typography>
                </Grid>}
                {! startingView &&
                <Grid item xs={12}>
                  <PageTitle title="Dashboard" subtitle={dashSubtitle}/>
                </Grid>}

                {!newActivationOn && 
                  (acctType === 3 || acctType === "3") && 
                  (theAccount.accountStatus === 1 || theAccount.accountStatus === "1" || theAccount.accountStatus === 2 || theAccount.accountStatus === "2") &&
                <AccountSuspendMsg 
                  accountType={acctType} 
                  accountStatus={theAccount.accountStatus}
                />}

                {newActivationOn && (theAccount.onboardingAccountTByAccountId && theAccount.onboardingAccountTByAccountId.onboardingId) &&
                <AccountActivationConfigurableSteps 
                  accountType={acctType} 
                  accountStatus={theAccount.accountStatus}
                  personaInquiryId={theAccount.personaInquiryId}
                  personaStatus={theAccount.personaStatus}
                  verificationComplete={theAccount.verificationComplete}
                  auth={this.props.auth}
                  history={this.props.history}
                />}

                {newActivationOn && !theAccount.onboardingAccountTByAccountId &&
                <AccountActivationSteps 
                  accountType={acctType} 
                  accountStatus={theAccount.accountStatus}
                  personaInquiryId={theAccount.personaInquiryId}
                  personaStatus={theAccount.personaStatus}
                  verificationComplete={theAccount.verificationComplete}
                  auth={this.props.auth}
                  history={this.props.history}
                />}

                {acctType === 3 &&
                <ResellerProblems history={this.props.history} auth={this.props.auth}/>}

                <Grid container spacing={2} className={classes.mainContainer}>
                  {vpps != null && vpps.length > 0 &&
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Grid container>
                            <Grid item xs={12}>
                              <Typography variant="h6">Partner programs</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                What are my partner programs?
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        {vpps.map(n => {
                          let theLogo = n.vppTByVppId.accountTByVendorId.logo;
                          let theProgram = n.vppTByVppId.programName;
                          let theDescription = n.vppTByVppId.description;
                          let aUrl = 'vendorpartnerprogram?vppId=' + n.vppId;
                          let stage = n.stage;

                          return (
                            <Grid item key={n.vppId}>
                              <Paper className={classes.dbTile} onClick={this.goTo.bind(this, aUrl)}>
                                <div className={classes.programInfo}>
                                  <Grid container spacing={2}>
                                    {theLogo != null &&
                                    <Grid item>
                                      <img alt="Program" height="48px" width="48px" src={theLogo}/>
                                    </Grid>}
                                    <Grid item style={{maxWidth: '260px'}}>
                                      <Typography variant="h6">{theProgram}</Typography>
                                      <Typography variant="body2">{theDescription}</Typography>
                                    </Grid>
                                  </Grid>
                                </div>
                                <div style={{float: 'right', marginTop: '4px', clear: 'right'}}>
                                  {/* Stage: 0: Lead.  1: Applied. 2: Active. 3: Suspended.  4: Rejected.  5: Left'*/}
                                  {stage === 0 &&
                                  <Tooltip title="You've looked at this program, but haven't joined yet">
                                    <Flag htmlColor={statusCaution}/>
                                  </Tooltip>}
                                  {stage === 1 &&
                                  <Tooltip title="Your request to join this program is being reviewed">
                                    <HourglassEmpty htmlColor={statusCaution}/>
                                  </Tooltip>}
                                  {stage === 2 &&
                                  <Tooltip title="You are a member of this program">
                                    <CheckCircle htmlColor={statusOk}/>
                                  </Tooltip>}
                                  {stage === 3 &&
                                  <Tooltip title="Your membership to this program is on hold">
                                    <PauseCircleFilled htmlColor={statusCaution}/>
                                  </Tooltip>}
                                  {stage === 4 &&
                                  <Tooltip title="Your membership to this program was rejected">
                                    <Cancel htmlColor={statusSuspended}/>
                                  </Tooltip>}
                                  {stage === 5 &&
                                  <Tooltip title="You've left this program">
                                    <Cancel htmlColor={statusOk}/>
                                  </Tooltip>}
                                </div>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  </Grid>}


                  {! startingView && showExpensing === true && theAccount.walletTsByAccountId.totalCount === 0 &&
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          {theAccount.useBudgets >= 1 &&
                          <Typography variant="h6">Your AppBind wallet balance</Typography>}
                          {theAccount.useBudgets === 0 &&
                          <Typography variant="h6">Your AppBind balance</Typography>}
                          {theAccount.useBudgets >= 1 &&
                          <Typography variant="body2">
                            Subscriptions and services are paid from your wallet available balance.  AppBind also may provide you with an overdraft float that allows purchases to exceed your available balance.
                          </Typography>}
                          {theAccount.useBudgets === 0 &&
                          <Typography variant="body2">
                            AppBind provides you with a float for to pay for subscriptions and services. You may also use your available balance to pay.
                          </Typography>}
                        </Grid>
                      
                        <Grid item xs={12}>
                          <BalanceAndFloatWidgets 
                            auth={this.props.auth} 
                            showEditLink={true} 
                            payFromBalance={payFromBalance} 
                            autoTopupAmount={autoTopupAmount} 
                            autoTopupThreshold={autoTopupThreshold} 
                            showLimitRequest={false} 
                            floatLimit={theAccount.floatLimit} 
                            accountId={acctId}
                            accountType={acctType}
                            useBudgets={theAccount.useBudgets} 
                            history={this.props.history}/>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>}

                  {! startingView && showExpensing === true && theAccount.walletTsByAccountId.totalCount > 0 &&
                  <Grid item xs={12}>
                    <Grid container spacing={2}>

                      <Grid item xs={12}>

                        <Paper className={classes.paper}>
                            <Grid container spacing={2} justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Typography variant="h6" className={classes.sectionTitle}>Your earnings balance</Typography>
                                <Typography variant="body2">
                                  You earn revenue from services you sell, markups on subscriptions, and partner program rebates.  Earnings can be paid out to
                                  your bank account, or be used to fund expense wallets that pay for subscriptions.
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <ProjectBalanceAndFloatWidgets
                                  auth={this.props.auth}
                                  autoTopupAmount={autoTopupAmount}
                                  autoTopupThreshold={autoTopupThreshold}
                                  floatLimit={theAccount.floatLimit}
                                  accountId={acctId}
                                  accountType={acctType}
                                  useBudgets={this.props.useBudgets}
                                  narrow={true}
                                />
                              </Grid>
                            </Grid>
                        </Paper>

                      </Grid>
                      <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container  spacing={2} justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Typography variant="h6" className={classes.sectionTitle}>Your expense wallet</Typography>
                                <Typography variant="body2">
                                  Subscriptions are paid from your expense wallet balance.  AppBind may also provide you with an overdraft float that allows purchases to exceed your available wallet balance.
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <WalletBalanceWidgets
                                  auth={this.props.auth}
                                  showEditLink={true} 
                                  walletId={theWallet.walletId}
                                  payFromBalance={payFromBalance} 
                                  autoTopupAmount={autoTopupAmount}
                                  autoTopupThreshold={autoTopupThreshold}
                                  showLimitRequest={false}
                                  floatLimit={theAccount.floatLimit}
                                  accountId={acctId}
                                  accountType={acctType}
                                  useBudgets={this.props.useBudgets}
                                  history={this.props.history}
                                  narrow={true}
                                />
                              </Grid>
                            </Grid> 
                          </Paper>
                        </Grid>
                    </Grid>
                  </Grid>}

                  { showExpensing === true &&
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      {paymentSourceCount === 0 &&
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography variant="h6">Funding</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            How will I pay for subscriptions?
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" className={classes.instructText}>
                            Go to <Button color="primary" size="small" variant="outlined" className={classes.instructButton}
                                          onClick={this.goTo.bind(this, 'fundingsources')}>
                                          <CreditCard className={classes.buttonicon}/> My Funding
                            </Button> to add a funding source. 
                          </Typography>
                        </Grid>
                      </Grid>}

                      {paymentSourceCount > 0 && paymentSourceProblems === true &&
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography variant="h6">Funding</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          {paymentSourceCount === 1 &&
                          <React.Fragment>
                            <Typography variant="body2">
                              We recommend that you also add a backup funding source.
                            </Typography>
                            <Typography variant="body2" className={classes.instructText}>
                                  Go to <Button color="primary"  size="small" variant="outlined" className={classes.instructButton}
                                        onClick={this.goTo.bind(this, 'fundingsources')}>
                                        <CreditCard className={classes.buttonicon}/> My Funding
                              </Button> to add a backup funding source. 
                            </Typography>
                          </React.Fragment>
                          }
                          {paymentSourceCount > 1 &&
                          <Typography variant="body2">
                            There are {paymentSourceCount} funding sources for subscriptions.
                          </Typography>
                          }
                        </Grid>

                        {paymentSourceNodes.map(n => {
                          return (
                          <Grid item className={classes.tileWrapper}>
                            <FundingSourceTile 
                              psType={n.psType}
                              ccBrand={n.ccBrand}
                              ccLast4={n.ccLast4}
                              ccExpy={n.ccExpy}
                              fiName={n.fiName}
                              achAccountNum={n.achAccountNum}
                              firstName={n.firstName}
                              lastName={n.lastName}
                              paypalEmail={n.paypalEmail}
                              declineCount={n.declineCount}
                              lastDeclineReason={n.lastDeclineReason}
                              lastDeclineChargeAmount={n.lastDeclineChargeAmount}
                              drawmode="dashboard"
                            />                        
                          </Grid>
                          )
                        })}
                      </Grid>}

                      {paymentSourceCount > 0 && paymentSourceProblems === false &&
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography variant="h6">Funding</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          {paymentSourceCount === 1 &&
                          <React.Fragment>
                            <Typography variant="body2">
                              We recommend that you also add a backup funding source.
                            </Typography>
                            <Typography variant="body2" className={classes.instructText}>
                                  Go to <Button color="primary"  size="small" variant="outlined" className={classes.instructButton}
                                        onClick={this.goTo.bind(this, 'fundingsources')}>
                                        <CreditCard className={classes.buttonicon}/> My Funding
                              </Button> to add a backup funding source. 
                            </Typography>
                          </React.Fragment>}
                          {paymentSourceCount > 1 &&
                          <Typography variant="body2">
                            There are {paymentSourceCount} funding sources for subscriptions.
                          </Typography>}
                        </Grid>
                        <Grid item className={classes.tileWrapper}>
                          <FundingSourceTile 
                            psType={paymentSourceNodes[0].psType}
                            ccBrand={paymentSourceNodes[0].ccBrand}
                            ccLast4={paymentSourceNodes[0].ccLast4}
                            ccExpy={paymentSourceNodes[0].ccExpy}
                            fiName={paymentSourceNodes[0].fiName}
                            achAccountNum={paymentSourceNodes[0].achAccountNum}
                            firstName={paymentSourceNodes[0].firstName}
                            lastName={paymentSourceNodes[0].lastName}
                            paypalEmail={paymentSourceNodes[0].paypalEmail}
                            drawmode="dashboard"
                          />                        
                        </Grid>
                        {paymentSourceCount > 1 &&
                        <Grid item className={classes.tileWrapper}>
                          <FundingSourceTile 
                            psType={paymentSourceNodes[1].psType}
                            ccBrand={paymentSourceNodes[1].ccBrand}
                            ccLast4={paymentSourceNodes[1].ccLast4}
                            ccExpy={paymentSourceNodes[1].ccExpy}
                            fiName={paymentSourceNodes[1].fiName}
                            achAccountNum={paymentSourceNodes[1].achAccountNum}
                            firstName={paymentSourceNodes[1].firstName}
                            lastName={paymentSourceNodes[1].lastName}
                            paypalEmail={paymentSourceNodes[1].paypalEmail}
                            drawmode="dashboard"
                          />                        
                        </Grid>}
                        {paymentSourceCount === 3 &&
                        <Grid item className={classes.tileWrapper}>
                          <FundingSourceTile 
                            psType={paymentSourceNodes[2].psType}
                            ccBrand={paymentSourceNodes[2].ccBrand}
                            ccLast4={paymentSourceNodes[2].ccLast4}
                            ccExpy={paymentSourceNodes[2].ccExpy}
                            fiName={paymentSourceNodes[2].fiName}
                            achAccountNum={paymentSourceNodes[2].achAccountNum}
                            firstName={paymentSourceNodes[2].firstName}
                            lastName={paymentSourceNodes[2].lastName}
                            paypalEmail={paymentSourceNodes[2].paypalEmail}
                            drawmode="dashboard"
                          />                        
                        </Grid>}
                        {paymentSourceCount > 3 &&
                        <Grid item className={classes.tileWrapper}>
                          <Paper>
                            <div className={classes.moretext}>
                              <Typography variant="body2" align="center">
                                ... {paymentSourceCount - 2} more funding sources ...
                              </Typography>
                            </div>
                          </Paper>
                        </Grid>}
                      </Grid>}
                    </Paper>
                  </Grid>}

                  {!startingView && showExpensing === true &&
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="h6">Your spending</Typography>
                          {theAccount.useBudgets >= 1 &&
                          <Typography variant="body2">
                            AppBind tracks your spending and provides budget and spend controls.
                          </Typography>}
                          {theAccount.useBudgets === 0 &&
                          <Typography variant="body2">
                            AppBind tracks your spending and provides spend controls.
                          </Typography>}
                        </Grid>
                      
                        <Grid item xs={12}>
                          <SpendingWidgets 
                            accountId={acctId} 
                            spendLimit={theBudget}
                            showEditLink={true}
                            history={this.props.history}/>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>}

                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      {subsCount === 0 &&
                      <Grid container>
                        <Grid item xs={12}>
                          {paymentSourceCount === 0 &&
                          <Typography variant="h6">Subscriptions</Typography>}
                          {paymentSourceCount > 0 &&
                          <Typography variant="h6">Subscriptions</Typography>}
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            What subscriptions am I managing for my customers?
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          {!subOrder &&
                            <Typography variant="body2" className={classes.instructText}>
                              Go to <Button color="primary" size="small" variant="outlined" className={classes.instructButton}
                                            onClick={this.goTo.bind(this, 'subscriptioncreate')}>
                                          <Add className={classes.buttonicon}/>Add subscription
                              </Button> to add a subscription. 
                            </Typography>
                          }
                          {subOrder &&
                            <Typography variant="body2" className={classes.instructText}>
                              Go to <Button color="primary" size="small" variant="outlined" className={classes.instructButton}
                                            onClick={this.goTo.bind(this, 'subscriptionorder')}>
                                          <Add className={classes.buttonicon}/>Add subscription
                              </Button> to add a subscription for a customer. 
                            </Typography>
                          }
                        </Grid>
                      </Grid>}

                      {subsCount > 0 &&
                      <React.Fragment>
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography variant="h6">Subscriptions</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            {subsCount === 1 &&
                            <Typography variant="body2">
                              There is one subscription.
                            </Typography>
                            }
                            {subsCount > 1 &&
                            <Typography variant="body2">
                              There are {subsCount} subscriptions.
                            </Typography>
                            }
                          </Grid>
                        </Grid>

                        {theApps != null && theApps.length > 0 &&
                        <TableContainer>
                          <Table className={classes.rtable}>
                            <TableHead className={classes.tblHead}>
                              <TableRow className={classes.tblHeadRow}>
                                <TableCell className={classes.tblHeadCell}>App</TableCell>
                                <TableCell className={classes.tblHeadCell}>Subscriptions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {theApps.map(n => {
                                subsShown++;
                                if (subsShown > 5) {
                                  // Do nothing
                                  return null;
                                } else if (subsShown <= 5) {
                                  remSubsCount = remSubsCount - Number(n.subCount);
                                  // Get list of accounts to show
                                  let acctList = n.acctList;
                                  let theLimit = 5
                                  let theLinks = [];
                                  let accountCounter = 0;
                                  for (let k=0; k<acctList.length; k++) {
                                    let acTxt = acctList[k];
                                    let acArr = acTxt.split("~~~");
                                    let thehref = null;
                                    if (acArr.length > 1) {
                                      thehref = "customer?accountId=" + acArr[1] + "&fp=Dashboard";
                                    }

                                    if (k >= theLimit) {
                                      let theDiff = acctList.length - theLimit;
                                      if (theDiff > 1) {
                                        theLinks.push(", ");
                                        if (thehref != null) {
                                          theLinks.push(<Link key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                        } else {
                                          theLinks.push({acTxt});
                                        }
                                        theLinks.push(', and ' + theDiff + ' more');
                                      } else {
                                        theLinks.push(", and ");
                                        if (thehref != null) {
                                            theLinks.push(<Link key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                        } else {
                                          theLinks.push({acTxt});
                                        }
                                      }
                                      break;
                                    }

                                    if (theLinks.length === 0) {
                                      if (thehref != null) {
                                        theLinks.push(<Link  key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                      } else {
                                        theLinks.push({acTxt});
                                      }
                                    } else if (k === acctList.length - 1) {
                                      theLinks.push(", and ");
                                      if (thehref != null) {
                                        theLinks.push(<Link  key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                      } else {
                                        theLinks.push({acTxt});
                                      }
                                    } else {
                                      theLinks.push(", ");
                                      if (thehref != null) {
                                        theLinks.push(<Link  key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                      } else {
                                        theLinks.push({acTxt});
                                      }
                                    }
                                  }
                                  let aUrl = "app?appId=" + n.appId + "&fp=Dashboard";

                                  return (
                                    <TableRow key={n.appId}>
                                      <TableCell>
                                        <Grid container alignItems="center" className={classes.appgrid}>
                                          <Grid item>
                                            <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px' style={{marginTop: '4px'}}/>
                                          </Grid>
                                          <Grid item className={classes.appnameitem}>
                                            <Link component="button" 
                                                  variant="body2"
                                                  onClick={this.goTo.bind(this, aUrl)}>
                                              {n.appName}
                                            </Link>
                                            {n.appSku != null && n.appSku !== '' && n.appSku !== n.appName &&
                                            <Typography variant="body2">{n.appSku}</Typography>
                                            }
                                          </Grid>
                                        </Grid>
                                      </TableCell>
                                      <TableCell>
                                        {n.subCount !== 1 &&
                                        <Typography variant="body2">{n.subCount} subscriptions ({theLinks})</Typography>}
                                        {n.subCount === 1 &&
                                        <Typography variant="body2">{n.subCount} subscription ({theLinks})</Typography>}
                                      </TableCell>
                                    </TableRow>
                                  );
                                } else {
                                  let remAppsCount = theApps.length - 5;
                                  return (
                                    <TableRow key={'moresubs'}>
                                      <TableCell className={classes.tblSumCell}>
                                        <Typography variant="body2" className={classes.tblSumTxt}>{remAppsCount} more apps</Typography>
                                      </TableCell>
                                      <TableCell className={classes.tblSumCell}>
                                        <Typography variant="body2" className={classes.tblSumTxt}>{remSubsCount} more subscriptions</Typography>
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>}
                      </React.Fragment>}
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      {customerCount === 0 &&
                      <Grid container>
                        <Grid item xs={12}>
                          {(paymentSourceCount === 0 || subsCount === 0) &&
                          <Typography variant="h6">Customers</Typography>}
                          {(paymentSourceCount > 0 && subsCount > 0) &&
                          <Typography variant="h6">Customers</Typography>}
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            Who are my customers?
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" className={classes.instructText}>
                              Go to <Button color="primary" size="small" variant="outlined" className={classes.instructButton}
                                            onClick={this.goTo.bind(this, 'customercreate')}>
                                            <People className={classes.buttonicon}/>Customers
                              </Button> to add a customer. 
                            </Typography>
                        </Grid>
                      </Grid>}

                      {customerCount > 0 &&
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography variant="h6">Customers</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          {customerCount === 1 &&
                          <Typography variant="body2">
                            You've added a customer.
                          </Typography>
                          }
                          {customerCount > 1 &&
                          <Typography variant="body2">
                            You've added {customerCount} customers.
                          </Typography>
                          }
                        </Grid>
                      </Grid>}

                      {customerCount > 0 && theCusts != null && theCusts.length > 0 &&
                      <TableContainer>
                        <Table className={classes.rtable}>
                          <TableHead className={classes.tblHead}>
                            <TableRow className={classes.tblHeadRow}>
                              <TableCell className={classes.tblHeadCell}>Customer</TableCell>
                              <TableCell className={classes.tblHeadCell}>Contact</TableCell>
                              { showExpensing === true &&
                              <TableCell className={classes.tblHeadCell}>Funding Source</TableCell>}
                              <TableCell className={classes.tblHeadCell}>CC on file</TableCell>
                              <TableCell className={classes.tblHeadCell}>Subscriptions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {theCusts.map(n => {
                              custsShown++;
                              if (custsShown > 5) {
                                // Do nothing
                                return null;
                              } else if (custsShown <= 5) {
                                let fsText;
                                if (n.psCount === 1) {
                                  fsText = n.psCount + ' funding source on file';
                                } else if (n.psCount === 0) {
                                  fsText = 'No funding sources on file';
                                } else {
                                  fsText = n.psCount + ' funding sources on file';
                                }

                                let ccOnFileText;
                                if (n.fCount === 1) {
                                  ccOnFileText = n.fCount + ' card on file';
                                } else if (n.fCount === 0) {
                                  ccOnFileText = 'No cards on file';
                                } else {
                                  ccOnFileText = n.fCount + ' cards on file';
                                }

                                let subText;
                                if (n.subCount === 1) {
                                  subText = n.subCount + ' subscription';
                                } else {
                                  subText = n.subCount + ' subscriptions';
                                }

                                let aUrl = "customer?accountId=" + n.custId + "&fp=Dashboard";
                                return (
                                  <TableRow key={n.custName}>
                                    <TableCell>
                                      <Link component="button" 
                                            variant="body2"
                                            className={classes.custlink}
                                            onClick={this.goTo.bind(this, aUrl)}>
                                        {n.custName}
                                      </Link>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" data-recording-sensitive>{n.custContact}</Typography>
                                    </TableCell>
                                    { showExpensing === true &&
                                    <TableCell>
                                      <Typography variant="body2">{fsText}</Typography>
                                    </TableCell>}
                                    <TableCell>
                                      <Typography variant="body2">{ccOnFileText}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{subText}</Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              } else {
                                let notShownCusts = theCusts.length - 4;
                                let aUrl = "customers";
                                return (
                                  <TableRow key={'morecusts'}>
                                    <TableCell colSpan={4} className={classes.tblSumCell}>
                                      <Typography variant="body2" className={classes.tblSumTxt}>
                                        <Link href={aUrl}>{notShownCusts} more customers</Link>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>}
                    </Paper>
                  </Grid>
                </Grid>
              </React.Fragment>
            );
          }
          return <LoadingSpinner />
        }}
      />
    );
  }
}

export default withStyles(styleSheet)(Dashboard);
